import Candidate from '@/orm/models/Candidate/Candidate';
import OfficeInterview from '@/orm/models/OfficeInterview/OfficeInterview';
import {
  candidateStatus,
  interviewStatusType,
  notificationType,
} from '@/enums';
import { computed, ref } from '@vue/composition-api';
import Vacancy from '@/orm/models/Vacancy/Vacancy';
import CandidateTest from '@/orm/models/Candidate/CandidateTest';

const showCandidateInterviewType = ref(null);

export default function useCandidates() {
  const metaData = computed(() => Candidate.getMetaData());
  const candidates = computed(() => {
    return Candidate.query().withAllRecursive().orderBy('id', 'desc').get();
  });

  function getCandidatesByVacancyId(vacancyRef) {
    return computed(() => {
      const vacancy = typeof vacancyRef === 'function' ? vacancyRef() : vacancyRef?.value ?? vacancyRef;
      const id = vacancy?.id ?? vacancy;
      if (!id) {
        return [];
      }
      return Candidate.query()
        .withAllRecursive()
        .where('vacancyId', id)
        .orderBy('id', 'desc')
        .get();
    });
  }

  function getTestResult(candidateId) {
    return computed(() => CandidateTest.query().withAllRecursive().where('id', candidateId).first());
  }

  async function fetchAll(params) {
    return Candidate.index(params);
  }

  function fetchCandidatesWithOrder(orderBy, order, ids = null) {
    const scopedIds = ids?.length ? ids : null;

    if (!orderBy || !order) {
      if (scopedIds) {
        return scopedIds
          .map((id) => Candidate.query().withAllRecursive().find(id))
          .filter(Boolean);
      }

      return Candidate.query().withAllRecursive().orderBy('id', 'desc').get();
    }

    let query = Candidate.query().withAllRecursive();

    if (scopedIds) {
      query = query.where('id', scopedIds);
    }

    if (orderBy === 'vacancyName') {
      return query
        .orderBy((candidate) => {
          const vacancy = Vacancy.query().find(candidate.vacancyId);
          return vacancy?.title;
        }, order)
        .get();
    }

    if (orderBy === 'fullName') {
      return query
        .orderBy((candidate) => {
          return `${candidate.firstName} ${candidate.lastName}`;
        }, order)
        .get();
    }

    return query.orderBy(orderBy, order).get();
  }

  async function deleteAll() {
    await Candidate.deleteAll();
  }

  async function fetchById(id) {
    await Candidate.api().fetchById(id);
  }

  async function updateWithoutSave(id, payload) {
    return Candidate.api().updateWithoutSave(id, payload);
  }

  async function updateNotificationMethod(id, method) {
    return updateWithoutSave(id, {
      notificationMethod: method,
    });
  }

  async function updateStatus(id, status) {
    await Candidate.api().updateStatus(id, status);
  }

  async function inviteCandidate(id, status) {
    return updateStatus(id, status);
  }

  async function fetchCandidatesByVacancyId(vacancyId) {
    return Candidate.api().fetchAllByVacancyId(vacancyId);
  }

  async function importCandidates(vacancyId, payload) {
    return Candidate.api().importCandidates(vacancyId, payload);
  }

  async function importCandidatesWithoutInvitation(vacancyId, payload) {
    return Candidate.api().importCandidatesWithoutInvitation(vacancyId, payload);
  }

  function resolveNotificationTypeFromStatus(status) {
    if (status === candidateStatus.INOFFICE_INTERVIEW) {
      return notificationType.CANDIDATE_INOFFICE_INTERVIEW;
    }

    if (status === candidateStatus.JOB_OFFER) {
      return notificationType.CANDIDATE_JOB_OFFER;
    }

    if (status === candidateStatus.DECLINED) {
      return notificationType.CANDIDATE_DECLINED;
    }

    return notificationType.CANDIDATE_COMMON;
  }

  async function storeVideoCall(id) {
    return OfficeInterview.api().storeVideoCall(id);
  }

  async function inviteToOnlineInterview(id) {
    return Candidate.api().inviteToOnlineInterview(id);
  }

  async function checkIfAlreadyHasOfficeInterview(candidate, type) {
    const searchedInterviews = candidate.officeInterviews
      .filter((interview) => interview.type === type)
      .sort((prev, next) => (prev.id < next.id ? 1 : -1));

    if (searchedInterviews.length === 0) {
      return false;
    }

    const searchedInterview = searchedInterviews[0];

    return searchedInterview.status === interviewStatusType.BLANK
      || searchedInterview.status === interviewStatusType.RECORDING;
  }

  async function checkIfAlreadyHasOnlineInterview(candidate) {
    return !!candidate.interview;
  }

  function getLastOfficeInterviewByType(candidate, type) {
    const searchedInterviews = candidate.officeInterviews
      .filter((interview) => interview.type === type)
      .sort((prev, next) => (prev.id < next.id ? 1 : -1));

    if (searchedInterviews.length === 0) {
      return null;
    }

    return searchedInterviews[0];
  }

  async function openCandidate(candidateId) {
    await Candidate.api().open(candidateId);
  }

  function findCandidateById(id) {
    return Candidate.query()
      .find(id);
  }

  async function exportCandidates(payload) {
    return Candidate.api().exportCandidates(payload);
  }

  async function getCandidateTest(id) {
    await CandidateTest.api().fetchCandidateTest(id);
  }

  async function sendNotifyMass(payload) {
    await Candidate.api().notifyMass(payload);
  }

  async function updateStatusMass(payload) {
    await Candidate.api().updateStatusMass(payload);
  }

  async function fetchPaginatedCandidates(id, payload) {
    return Candidate.api().fetchPaginatedCandidates(id, payload);
  }

  return {
    updateWithoutSave,
    updateNotificationMethod,
    updateStatus,
    inviteCandidate,
    resolveNotificationTypeFromStatus,
    fetchCandidatesByVacancyId,
    getCandidatesByVacancyId,
    storeVideoCall,
    importCandidates,
    importCandidatesWithoutInvitation,
    inviteToOnlineInterview,
    showCandidateInterviewType,
    checkIfAlreadyHasOfficeInterview,
    getLastOfficeInterviewByType,
    checkIfAlreadyHasOnlineInterview,
    openCandidate,
    fetchAll,
    deleteAll,
    candidates,
    metaData,
    fetchById,
    findCandidateById,
    fetchCandidatesWithOrder,
    exportCandidates,
    getCandidateTest,
    getTestResult,
    sendNotifyMass,
    updateStatusMass,
    fetchPaginatedCandidates,
  };
}
